"use client";

import React, { useRef, useState, useEffect } from 'react';
import styles from './_gc-step-animated-card.module.scss';

/**
 * @description GC Calculator — Animated Step Card
 * Wraps a pill and its expanded step content to orchestrate a multi-phase
 * expand/collapse animation:
 *
 * EXPAND:  pill content fades out → bg turns white + height grows → step fades in
 * COLLAPSE: step content fades out → height shrinks + bg turns purple → pill fades in
 *
 * During any animation phase, pointer-events are disabled to prevent edits.
 */

// Timing (ms)
const FADE_OUT_MS   = 250;
const RESIZE_MS     = 333;
const FADE_IN_MS    = 250;

// Animation phases
const PHASE = {
  IDLE_PILL:           'idle-pill',
  EXPAND_FADE_OUT:     'expand-fade-out',
  EXPAND_RESIZE:       'expand-resize',
  EXPAND_FADE_IN:      'expand-fade-in',
  IDLE_EXPANDED:       'idle-expanded',
  COLLAPSE_FADE_OUT:   'collapse-fade-out',
  COLLAPSE_RESIZE:     'collapse-resize',
  COLLAPSE_FADE_IN:    'collapse-fade-in',
};

function GcStepAnimatedCard({ isExpanded, pill, children, onAnimationComplete, variant = 'default' }) {
  const [phase, setPhase] = useState(
    isExpanded ? PHASE.IDLE_EXPANDED : PHASE.IDLE_PILL
  );

  const containerRef = useRef(null);
  const pillRef      = useRef(null);
  const stepRef      = useRef(null);
  const timerRef     = useRef(null);

  const isAnimating = phase !== PHASE.IDLE_PILL && phase !== PHASE.IDLE_EXPANDED;

  // Cleanup timers on unmount
  useEffect(() => () => { clearTimeout(timerRef.current); }, []);

  // Respond to isExpanded prop changes
  useEffect(() => {
    if (isExpanded && phase === PHASE.IDLE_PILL) {
      setPhase(PHASE.EXPAND_FADE_OUT);
    } else if (!isExpanded && phase === PHASE.IDLE_EXPANDED) {
      setPhase(PHASE.COLLAPSE_FADE_OUT);
    }
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drive the animation state machine
  useEffect(() => {
    clearTimeout(timerRef.current);

    switch (phase) {
      case PHASE.EXPAND_FADE_OUT:
        // Wait for pill fade-out animation to finish
        timerRef.current = setTimeout(() => {
          setPhase(PHASE.EXPAND_RESIZE);
        }, FADE_OUT_MS);
        break;

      case PHASE.EXPAND_RESIZE:
        // Step layer is now in DOM (hidden). Measure its natural height and
        // animate the container from current (pill) height to target.
        requestAnimationFrame(() => {
          if (!containerRef.current || !stepRef.current) {
            setPhase(PHASE.EXPAND_FADE_IN);
            return;
          }
          const currentHeight = containerRef.current.offsetHeight;
          const targetHeight = stepRef.current.scrollHeight;
          containerRef.current.style.height = currentHeight + 'px';
          // Force reflow so the browser registers the starting height
          containerRef.current.offsetHeight; // eslint-disable-line no-unused-expressions
          containerRef.current.style.height = targetHeight + 'px';

          timerRef.current = setTimeout(() => {
            if (containerRef.current) {
              // If the card's top scrolled above the viewport, bring it back
              const rect = containerRef.current.getBoundingClientRect();
              if (rect.top < 0) {
                containerRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
              }
            }
            setPhase(PHASE.EXPAND_FADE_IN);
          }, RESIZE_MS);
        });
        break;

      case PHASE.EXPAND_FADE_IN:
        // Safe to clear height — pill is removed from DOM, step is now in flow
        if (containerRef.current) containerRef.current.style.height = '';
        timerRef.current = setTimeout(() => {
          setPhase(PHASE.IDLE_EXPANDED);
          onAnimationComplete?.();
        }, FADE_IN_MS);
        break;

      case PHASE.COLLAPSE_FADE_OUT:
        timerRef.current = setTimeout(() => {
          setPhase(PHASE.COLLAPSE_RESIZE);
        }, FADE_OUT_MS);
        break;

      case PHASE.COLLAPSE_RESIZE:
        // Pill layer is now in DOM (hidden). Measure its height and shrink.
        requestAnimationFrame(() => {
          if (!containerRef.current || !pillRef.current) {
            setPhase(PHASE.COLLAPSE_FADE_IN);
            return;
          }
          const currentHeight = containerRef.current.offsetHeight;
          const targetHeight = pillRef.current.scrollHeight;
          containerRef.current.style.height = currentHeight + 'px';
          containerRef.current.offsetHeight; // eslint-disable-line no-unused-expressions
          containerRef.current.style.height = targetHeight + 'px';

          timerRef.current = setTimeout(() => {
            setPhase(PHASE.COLLAPSE_FADE_IN);
          }, RESIZE_MS);
        });
        break;

      case PHASE.COLLAPSE_FADE_IN:
        // Safe to clear height — step is removed from DOM, pill is now in flow
        if (containerRef.current) containerRef.current.style.height = '';
        timerRef.current = setTimeout(() => {
          setPhase(PHASE.IDLE_PILL);
          onAnimationComplete?.();
        }, FADE_IN_MS);
        break;

      default:
        break;
    }
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Determine what layers to render ────────────────────────────────────────
  // Both layers are rendered during resize phases so heights can be measured
  // fresh (source in-flow for container height, target absolute for measurement).
  const renderPill = [
    PHASE.IDLE_PILL,
    PHASE.EXPAND_FADE_OUT,
    PHASE.EXPAND_RESIZE,
    PHASE.COLLAPSE_RESIZE,
    PHASE.COLLAPSE_FADE_IN,
  ].includes(phase);

  const renderStep = [
    PHASE.IDLE_EXPANDED,
    PHASE.EXPAND_RESIZE,
    PHASE.EXPAND_FADE_IN,
    PHASE.COLLAPSE_FADE_OUT,
    PHASE.COLLAPSE_RESIZE,
  ].includes(phase);

  // ── Layer classes ──────────────────────────────────────────────────────────
  const pillClass = [
    styles.layer,
    styles.pill_layer,
    phase === PHASE.EXPAND_FADE_OUT ? styles.fade_out : '',
    phase === PHASE.EXPAND_RESIZE ? styles.layer_hidden : '',
    phase === PHASE.COLLAPSE_RESIZE ? styles.measure_layer : '',
    phase === PHASE.COLLAPSE_FADE_IN ? styles.fade_in : '',
  ].filter(Boolean).join(' ');

  const stepClass = [
    styles.layer,
    styles.step_layer,
    phase === PHASE.COLLAPSE_FADE_OUT ? styles.fade_out : '',
    phase === PHASE.COLLAPSE_RESIZE ? styles.layer_hidden : '',
    phase === PHASE.EXPAND_RESIZE ? styles.measure_layer : '',
    phase === PHASE.EXPAND_FADE_IN ? styles.fade_in : '',
  ].filter(Boolean).join(' ');

  // ── Background ─────────────────────────────────────────────────────────────
  const bgWhite = [
    PHASE.EXPAND_FADE_OUT,
    PHASE.EXPAND_RESIZE,
    PHASE.EXPAND_FADE_IN,
    PHASE.IDLE_EXPANDED,
    PHASE.COLLAPSE_FADE_OUT,
  ].includes(phase);

  const containerClass = [
    styles.animated_card,
    bgWhite ? styles.bg_white : (variant === 'disabled' ? styles.bg_disabled : ''),
    isAnimating ? styles.no_interact : '',
    phase === PHASE.EXPAND_RESIZE || phase === PHASE.COLLAPSE_RESIZE ? styles.resizing : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={containerRef} className={containerClass}>
      {renderPill && (
        <div ref={pillRef} className={pillClass}>
          {pill}
        </div>
      )}
      {renderStep && (
        <div ref={stepRef} className={stepClass}>
          {children}
        </div>
      )}
    </div>
  );
}

export default GcStepAnimatedCard;
